<template>
  <div class="sync-points-view">

    <div class="form-container">
      <h2>Sync your Utah draw points for an easier experience</h2>
      <p>To sync points in Utah, we need</p>
      <!-- Instructions -->
      <ul>

        <li>Your birth date (mm/dd/yyyy).</li>
        <li>Your <a href="https://www.utah-hunt.com/" target="_blank">Utah hunting</a> customer ID.
          <ul>
            <li>Can be found on any previous hunting license, or you can use your Social Security Number</li>
            <li>(We will not save or log your SSN on our systems)</li>
          </ul>
        </li>
        <li>Your permission to retrieve your hunting data.</li>
      </ul>

      <!-- Date of Birth Input -->
      <div class="input-group">
        <label for="dateOfBirth">Date of Birth (mm/dd/yyyy):</label>
        <input id="dateOfBirth" type="text" v-model="dateOfBirthString" @input="formatAndValidateDateInput"
          placeholder="Enter your birth date" />
        <span v-if="!isValidDate && dateOfBirthString" class="error-text">Invalid date format. Use mm/dd/yyyy.</span>
      </div>

      <!-- Customer ID or SSN Input -->
      <div class="input-group">
        <label for="customerIdOrSSN">Customer ID:</label>
        <input id="customerIdOrSSN" type="password" v-model="customerIdOrSSN"
          placeholder="Utah DNR customer ID (or SSN)" />
      </div>

      <!-- Terms and Conditions Agreement -->
      <div class="input-group" style="display: flex; align-items: center;">
        <input type="checkbox" id="termsCheckbox" v-model="termsAndConditionsAccepted" style="width: 30px;" />
        <label style="margin: 0;">
          I agree to the
          <span class="terms-link" @click="showTermsModal = true">Terms and Conditions</span>
        </label>
      </div>

      <!-- Terms and Conditions Modal -->
      <TermsAndConditionsView :showModal=showTermsModal v-if="showTermsModal" @close="showTermsModal = false">
      </TermsAndConditionsView>
      <!-- Sync Now Button -->
      <button :disabled="!isValidDate || !customerIdOrSSN || !termsAndConditionsAccepted" @click="submitStateSync">
        <span v-if="isLoading">Syncing...</span>
        <span v-else>Sync</span>
      </button>

      <!-- Sync Status Messages -->
      <p v-if="successfulSync" class="success-text">Sync successful! Check your dashboard for updated data.</p>
      <p v-if="syncFailed" class="error-text">Sync failed. Please try again later.</p>

      <button type="button" class="btn btn-link w-100 mt-2" @click="$emit('showHome')">
        Skip For Now
      </button>
    </div>
  </div>
</template>

<script>
import TermsAndConditionsView from "./TermsAndConditionsView.vue";
import { mapActions, mapState } from "vuex";

export default {
  name: "SyncPointsViewUtah",
  components: {
    TermsAndConditionsView,
  },
  computed: {
    ...mapState({
      user: state => state.user,
      isAuthenticated: state => state.isAuthenticated
    }),
  },
  data() {
    return {
      dateOfBirthString: "",
      customerIdOrSSN: "",
      isValidDate: false,
      convertedDate: null,
      termsAndConditionsAccepted: false,
      isLoading: false,
      showTermsModal: false,
      successfulSync: false,
      syncFailed: false,
    };
  },
  created() {
    if (this.isAuthenticated && !this.user) {
      this.fetchUserData();
    } else if (this.user && this.user.dateOfBirth) {
      const date = new Date(this.user.dateOfBirth);
      const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
      const day = date.getUTCDate().toString().padStart(2, '0');
      const year = date.getUTCFullYear();
      this.dateOfBirthString = `${month}/${day}/${year}`;
      this.formatAndValidateDateInput();
    }
  },
  methods: {
    ...mapActions(['setDrawPoints', 'setUser']),
    async fetchUserData() {
      try {
        const response = await fetch(this.$API_BASE_URL + '/api/customers/whoami', {
          method: 'GET',
          credentials: 'include',
        });
        if (response.ok) {
          const userData = await response.json();
          this.setUser(userData);
          if (userData.dateOfBirth) {
            const date = new Date(userData.dateOfBirth);
            const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
            const day = date.getUTCDate().toString().padStart(2, '0');
            const year = date.getUTCFullYear();
            this.dateOfBirthString = `${month}/${day}/${year}`;
            this.formatAndValidateDateInput();
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    },
    exitTermsAndConditions() {
      if (this.showTermsModal) {
        this.showTermsModal = false;
      }
    },
    // Formats and validates the date input
    formatAndValidateDateInput() {
      let input = this.dateOfBirthString.replace(/[^0-9]/g, ""); // Remove non-numeric characters

      if (input.length > 2) input = input.slice(0, 2) + "/" + input.slice(2);
      if (input.length > 5) input = input.slice(0, 5) + "/" + input.slice(5);

      this.dateOfBirthString = input.slice(0, 10); // Limit to 10 characters

      // Validate the date format
      const [month, day, year] = this.dateOfBirthString.split("/");
      if (month && day && year) {
        const date = new Date(`${year}-${month}-${day}`);
        if (!isNaN(date)) {
          this.isValidDate = true;
          this.convertedDate = date;
          return;
        }
      }
      this.isValidDate = false;
      this.convertedDate = null;
    },
    // Submits the state sync request
    async submitStateSync() {
      if (!this.isValidDate || !this.customerIdOrSSN || !this.termsAndConditionsAccepted) {
        return;
      }

      this.isLoading = true;

      try {
        await this.syncData({
          state: "Utah",
          customerId: this.user.id,
          stateCustomerId: this.customerIdOrSSN,
          dateOfBirth: this.convertedDate,
        });
        if (this.successfulSync) {
          this.successfulSync = true;
        }
      } catch (error) {
        console.error("Failed to sync:", error);
        this.syncFailed = true;
      } finally {
        this.isLoading = false;
      }
    },
    async syncData({ state, customerId, stateCustomerId, dateOfBirth }) {
      try {
        const stateLoginObject = {
          state: state,
          customerId: customerId,
          customerStateId: stateCustomerId,
          dateOfBirth: dateOfBirth,
        };
        state = state.toUpperCase();
        const response = await fetch(this.$API_BASE_URL + '/api/drawpoints/customer/' + customerId + '/state/' + state, {
          method: 'POST',
          credentials: "include", // Ensures cookies are sent with the request
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(stateLoginObject)
        });
        if (response.ok) {
          const data = await response.json();
          this.setDrawPoints(data);
          this.successfulSync = true;
          this.$emit('showHome');
        }
        else {
          this.successfulSync = false;
          this.syncFailed = true;
        }
      } catch (error) {
        console.error('Error:', error);
      }
    },
  },
};
</script>

<style scoped>
.sync-points-view {
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.input-group {
  margin-bottom: 16px;
}

label {
  display: block;
  margin-bottom: 4px;
  font-weight: bold;
}

.terms-link {
  color: #007bff;
  text-decoration: underline;
  cursor: pointer;
}

.terms-link:hover {
  color: #0056b3;
}

input {
  width: 100%;
  padding: 8px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

button {
  width: 100%;
  padding: 10px;
  font-size: 16px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.error-text {
  color: red;
  font-size: 14px;
}

.success-text {
  color: green;
  font-size: 16px;
}

ul li a {
  text-decoration: none;
  color: #007bff;
}

ul li a:hover {
  text-decoration: underline;
  color: #0056b3;
}
</style>
